import { Order } from '../../shared/models/Order';
import { exportJsonSheetsToExcel } from './excel-export';
import {
  getBoletaExportLabel,
  getOrderAmountPaid,
  getOrderDeliveryPrice,
  getOrderItemsCount,
  getItemCategoryLabel,
  getItemModeloLabel,
  getOrderCategoryLabel,
  getOrderModeloLabel,
  getOrderProductLabel,
  getOrderTotalAmount,
  OrderBoletaExportStatus
} from './order-export';
import { formatMoananiDateTimeExport } from './peru-datetime';

function formatDateOnlyExport(dateStr: string, timeStr?: string): string {
  if (!dateStr) return '';
  const part = dateStr.split('T')[0];
  const [y, m, d] = part.split('-');
  if (!y || !m || !d) return dateStr;
  const formatted = `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  return timeStr ? `${formatted} ${timeStr}` : formatted;
}

function money2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export interface DeliveredSalesTotals {
  cobrado: number;
  ventas: number;
  envio: number;
}

function appendVentasResumen(
  rows: Record<string, unknown>[],
  totals: DeliveredSalesTotals
): void {
  rows.push({});
  rows.push({
    Cliente: 'RESUMEN (solo entregados)',
    Envío: money2(totals.envio),
    Adelanto: money2(totals.cobrado),
    'Total venta': money2(totals.ventas),
    Boleta: 'Envío es informativo; total cobrado = suma de adelantos'
  });
}

/**
 * Desglosa cada ítem en filas de cantidad 1.
 * Total pagado y Envío solo en la primera fila de la orden (para poder sumar sin duplicar).
 */
function mapDetalleProductos(orders: Order[], cobrado: number): {
  rows: Record<string, unknown>[];
  summaryRows: Record<string, unknown>[];
} {
  const rows: Record<string, unknown>[] = [];

  for (const o of orders) {
    const totalPagado = money2(getOrderAmountPaid(o));
    const envio = money2(getOrderDeliveryPrice(o));

    const rawItems = o.items?.length ? o.items : [];
    type UnitLine = { nombre: string; categoria: string; modelo: string; color: string; talla: string };
    const units: UnitLine[] = [];

    if (!rawItems.length) {
      units.push({
        nombre: getOrderProductLabel(o),
        categoria: getOrderCategoryLabel(o),
        modelo: getOrderModeloLabel(o),
        color: '—',
        talla: '—'
      });
    } else {
      for (const item of rawItems) {
        const anyItem = item as any;
        const nombre =
          anyItem?.product?.productName ||
          anyItem?.product?.name ||
          anyItem?.productName ||
          anyItem?.description ||
          'Producto';
        const qty = Math.max(1, Number(anyItem?.quantity) || 1);

        for (let i = 0; i < qty; i++) {
          units.push({
            nombre: String(nombre).trim() || 'Producto',
            categoria: getItemCategoryLabel(anyItem),
            modelo: getItemModeloLabel(anyItem),
            color: anyItem?.color ? String(anyItem.color) : '—',
            talla: anyItem?.size ? String(anyItem.size) : '—'
          });
        }
      }
    }

    units.forEach((u, idx) => {
      rows.push({
        'N° Orden': o.id,
        Cliente: o.clientName,
        Producto: u.nombre,
        Categoría: u.categoria,
        Modelo: u.modelo,
        Color: u.color,
        Talla: u.talla,
        Cantidad: 1,
        'Total pagado': idx === 0 ? totalPagado : '',
        Envío: idx === 0 ? envio : '',
        Estado: o.estados?.map((e: any) => e.nombreEstado).join(', ') || o.status
      });
    });
  }

  return {
    rows,
    summaryRows: [
      {
        Cliente: 'RESUMEN',
        'Total pagado': money2(cobrado),
        Envío: '',
        Producto: 'Suma de total pagado por orden (sin duplicar filas)'
      }
    ]
  };
}

/** Excel solo ventas manuales: hoja Ventas + hoja Detalle productos. */
export async function exportManualSalesExcel(
  orders: Order[],
  boletaMap: Record<number, OrderBoletaExportStatus>,
  totals: DeliveredSalesTotals
): Promise<void> {
  if (!orders.length) {
    throw new Error('No hay ventas manuales para exportar.');
  }

  const ventas = orders.map(o => ({
    'Fecha registro': formatMoananiDateTimeExport(o.createdAt || ''),
    'Fecha entrega': formatDateOnlyExport(o.scheduledDate || '', o.scheduledTime || undefined),
    'N° Orden': o.id,
    Cliente: o.clientName,
    Entrega: o.tipoEnvio?.nombreTipoEnvio || o.deliveryMethod,
    Producto: getOrderProductLabel(o),
    Categoría: getOrderCategoryLabel(o),
    Modelo: getOrderModeloLabel(o),
    'Cant. ítems': getOrderItemsCount(o),
    'Estado Final': o.estados?.map((e: any) => e.nombreEstado).join(', ') || o.status,
    Envío: getOrderDeliveryPrice(o),
    Adelanto: getOrderAmountPaid(o),
    'Total venta': getOrderTotalAmount(o),
    Boleta: getBoletaExportLabel(o.id, boletaMap),
    Observación: (o as any).notes || ''
  }));
  appendVentasResumen(ventas, totals);

  const detalle = mapDetalleProductos(orders, totals.cobrado);

  await exportJsonSheetsToExcel(
    [
      { name: 'Ventas', rows: ventas },
      {
        name: 'Detalle productos',
        rows: detalle.rows,
        summaryRows: detalle.summaryRows
      }
    ],
    `historial_ventas_manuales_${Date.now()}.xlsx`
  );
}

/** Excel solo ventas web: hoja Ventas + hoja Detalle productos. */
export async function exportWebSalesExcel(
  orders: Order[],
  boletaMap: Record<number, OrderBoletaExportStatus>,
  totals: DeliveredSalesTotals
): Promise<void> {
  if (!orders.length) {
    throw new Error('No hay ventas web para exportar.');
  }

  const ventas = orders.map(o => ({
    'N° Orden': o.id,
    Fecha: formatMoananiDateTimeExport(o.createdAt || ''),
    Cliente: o.clientName,
    Telefono: o.clientPhone,
    Entrega: o.tipoEnvio?.nombreTipoEnvio || o.deliveryMethod,
    Producto: getOrderProductLabel(o),
    Categoría: getOrderCategoryLabel(o),
    Modelo: getOrderModeloLabel(o),
    'Cant. ítems': getOrderItemsCount(o),
    Estado: o.status,
    Envío: getOrderDeliveryPrice(o),
    Adelanto: getOrderAmountPaid(o),
    'Total venta': getOrderTotalAmount(o),
    Boleta: getBoletaExportLabel(o.id, boletaMap)
  }));
  appendVentasResumen(ventas, totals);

  const detalle = mapDetalleProductos(orders, totals.cobrado);

  await exportJsonSheetsToExcel(
    [
      { name: 'Ventas', rows: ventas },
      {
        name: 'Detalle productos',
        rows: detalle.rows,
        summaryRows: detalle.summaryRows
      }
    ],
    `historial_ventas_web_${Date.now()}.xlsx`
  );
}
